import { collection, getDocs, query, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '@config/firebase';

export interface EmailMessage {
  messageId: string;
  threadId: string;
  userId: string;
  timestamp: Date;
  sender: {
    email: string;
    name: string;
  };
  subject: string;
  content: {
    plain?: string;
    html?: string;
    extractedText?: string;
    formattedMessage?: string;
  };
  metadata?: {
    emailClient?: string;
    isReply?: boolean;
    hasAttachments?: boolean;
    direction?: 'inbound' | 'outbound';
    email_type?: string;
  };
}

export interface EmailThread {
  threadId: string;
  userId: string;
  userName: string;
  userEmail: string;
  subject: string;
  lastActivity: Date;
  createdAt: Date;
  email_type?: string;
  participants: string[];
  messages: EmailMessage[];
  messageCount: number;
}

class EmailService {
  /**
   * Fetch all email threads for a specific user
   * @param uid - User ID
   * @param userName - User's display name
   * @param userEmail - User's email
   * @returns Promise<EmailThread[]>
   */
  async fetchUserEmailThreads(uid: string, userName: string, userEmail: string): Promise<EmailThread[]> {
    try {
      const emailsRef = collection(db, 'private_user_data', uid, 'emails');
      const threadsSnapshot = await getDocs(emailsRef);

      if (threadsSnapshot.empty) {
        return [];
      }

      // Fetch messages for all threads in parallel (avoids N+1 sequential queries)
      const threadResults = await Promise.all(
        threadsSnapshot.docs.map(async (threadDoc) => {
          const threadData = threadDoc.data();
          const threadId = threadDoc.id;

          const messagesRef = collection(db, 'private_user_data', uid, 'emails', threadId, 'messages');
          const messagesQuery = query(messagesRef, orderBy('timestamp', 'desc'));
          const messagesSnapshot = await getDocs(messagesQuery);

          const messages: EmailMessage[] = messagesSnapshot.docs.map(msgDoc => {
            const msgData = msgDoc.data();
            return {
              messageId: msgDoc.id,
              threadId,
              userId: uid,
              timestamp: msgData.timestamp instanceof Timestamp
                ? msgData.timestamp.toDate()
                : new Date(msgData.timestamp),
              sender: {
                email: msgData.sender?.email || '',
                name: msgData.sender?.name || '',
              },
              subject: msgData.subject || threadData.subject || '(No Subject)',
              content: {
                plain: msgData.content?.plain || msgData.content?.extractedText || '',
                html: msgData.content?.html || '',
                extractedText: msgData.content?.extractedText || '',
                formattedMessage: msgData.content?.formattedMessage || '',
              },
              metadata: {
                emailClient: msgData.metadata?.emailClient,
                isReply: msgData.metadata?.isReply,
                hasAttachments: msgData.metadata?.hasAttachments,
                direction: msgData.metadata?.direction,
                email_type: msgData.metadata?.email_type || threadData.email_type,
              },
            };
          });

          const threadSubject = threadData.subject ||
            (messages.length > 0 ? messages[messages.length - 1].subject : '(No Subject)');

          return {
            threadId,
            userId: uid,
            userName,
            userEmail,
            subject: threadSubject,
            lastActivity: threadData.lastActivity instanceof Timestamp
              ? threadData.lastActivity.toDate()
              : new Date(threadData.lastActivity || Date.now()),
            createdAt: threadData.createdAt instanceof Timestamp
              ? threadData.createdAt.toDate()
              : new Date(threadData.createdAt || Date.now()),
            email_type: threadData.email_type,
            participants: threadData.participants || [],
            messages,
            messageCount: messages.length,
          } as EmailThread;
        })
      );

      // Sort threads by last activity
      threadResults.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());

      return threadResults;
    } catch (error: any) {
      if (error?.code === 'permission-denied') {
        return [];
      }
      throw error;
    }
  }

  /**
   * Fetch all email threads for multiple users (tenant users)
   * @param users - Array of user objects with uid, displayName, and email
   * @returns Promise<EmailThread[]>
   */
  async fetchEmailsForUsers(users: Array<{ uid: string; displayName: string; email: string }>): Promise<EmailThread[]> {
    const allThreads: EmailThread[] = [];

    const results = await Promise.allSettled(
      users.map(user => this.fetchUserEmailThreads(user.uid, user.displayName, user.email))
    );

    for (const result of results) {
      if (result.status === 'fulfilled' && result.value.length > 0) {
        allThreads.push(...result.value);
      }
    }

    // Sort all threads by last activity
    allThreads.sort((a, b) => b.lastActivity.getTime() - a.lastActivity.getTime());

    return allThreads;
  }
}

export const emailService = new EmailService();
export default emailService;
